import shpjs from "shpjs";
import { DANGER_MATRIX } from "../constants/dangerMatrix";
import { RISK_OUTLINE, RISK_PALETTE } from "../constants/riskPalette";
import { pickVegClassForPolygon } from "./utils";
import * as turf from "@turf/turf";
import { isValidPolygonGeom } from "./polygonUtils";

const __assetRiskThrottle = new Map();

const DEFAULT_BY_VEG = {
  other: 'low',
  grass: 'elevated',
  crop: 'elevated',
  scrub: 'high',
  wood: 'high',
};

const MAX_DN_PROXIMITY_KM = 100;

function rgba(hex, a) {
  const h = hex.replace('#', '');
  const v = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
  const r = (v >> 16) & 255, g = (v >> 8) & 255, b = v & 255;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

export const vegColorsByClass = (cls, finalRisk = 'low') => {
  const base = RISK_PALETTE[finalRisk] || RISK_PALETTE.low;
  const stroke = RISK_OUTLINE[finalRisk] || RISK_OUTLINE.low;
  return {
    fill: base,
    stroke,
    bufFill: rgba(base, 0.25),
    polyFill: rgba(base, 0.4),
    bufOutline: rgba(stroke, 0.8),
  };
};

export function tagAndPaintAssetRisk(map, draw, poly) {
  if (!poly?.id) return;

  const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
  const last = __assetRiskThrottle.get(poly.id) || 0;
  if (now - last < 60) return;
  __assetRiskThrottle.set(poly.id, now);

  (async () => {
    const vegClass = pickVegClassForPolygon(map, poly);

    const fireFC = await ensureFireDangerFC();
    const res = dnForAssetPolygonWithNearest(poly, fireFC);
    const useDN = res.mode === 'inside' || res.mode === 'intersect' ||
      (res.mode === 'nearest' && res.distKm <= MAX_DN_PROXIMITY_KM);

    const dnBand = res.dnBand;
    const finalRisk = useDN
      ? (DANGER_MATRIX[dnBand]?.[vegClass] || 'low')
      : (DEFAULT_BY_VEG[vegClass] || 'low');

    setAssetRiskProps(draw, poly.id, vegClass, finalRisk, res.dn, dnBand);
    draw.setFeatureProperty(poly.id, 'riskDNSource', res.mode);
    draw.setFeatureProperty(poly.id, 'riskDNNearestKm', isFinite(res.distKm) ? Number(res.distKm.toFixed(3)) : null);
    draw.setFeatureProperty(poly.id, 'riskDNUsed', useDN);
    draw.setFeatureProperty(poly.id, 'riskFinal', finalRisk);

    const colors = vegColorsByClass(vegClass, finalRisk);
    repaintExistingBuffers(map, poly.id, colors);
    refreshAssetPaintOverlay(map, draw);
  })().catch(err => console.error('[asset-risk] compute failed:', err));
}

function setAssetRiskProps(draw, featureId, vegClass, finalRisk, dn, dnBand) {
  const colors = vegColorsByClass(vegClass, finalRisk);
  draw.setFeatureProperty(featureId, 'vegClass', vegClass);
  draw.setFeatureProperty(featureId, 'riskDN', Number.isFinite(dn) ? Number(dn) : null);
  draw.setFeatureProperty(featureId, 'riskDNBand', dnBand);
  draw.setFeatureProperty(featureId, 'riskClass', finalRisk);

  draw.setFeatureProperty(featureId, 'riskFillColor', colors.fill);
  draw.setFeatureProperty(featureId, 'riskLineColor', colors.stroke);
  draw.setFeatureProperty(featureId, 'riskBufferFill', colors.bufFill);
  draw.setFeatureProperty(featureId, 'riskPolyFill', colors.polyFill);
  draw.setFeatureProperty(featureId, 'riskBufferOutline', colors.bufOutline);
}

function repaintExistingBuffers(map, polyId, colors) {
  if (!map) return;

  (map.getStyle()?.layers || [])
    .map(l => l.id)
    .filter(id => id.startsWith(`buffer-${polyId}-`) || id.startsWith(`buffer-outline-${polyId}-`))
    .forEach(id => {
      const isOutline = id.startsWith(`buffer-outline-${polyId}-`);
      if (map.getLayer(id)) {
        map.setPaintProperty(id, isOutline ? 'line-color' : 'fill-color',
          isOutline ? colors.bufOutline : colors.bufFill
        );
        if (!isOutline) {
          map.setPaintProperty(id, 'fill-outline-color', colors.bufOutline);
        }
      }
    });
}

let __fireDangerFC = null;

async function ensureFireDangerFC() {
  if (__fireDangerFC) return __fireDangerFC;
  const shpData = await shpjs("/data/scaled_poly")

  if (!shpData)
    return null;

  const sortedFeatures = shpData.features.sort((a, b) => {
    const dnA = Number(a.properties.DN);
    const dnB = Number(b.properties.DN);
    return dnA - dnB;
  });
  __fireDangerFC = { type: 'FeatureCollection', features: sortedFeatures };

  return __fireDangerFC;
}

function bandFromDN(dn) {
  const n = Number(dn);
  if (!Number.isFinite(n)) return 'low';
  if (n < 20) return 'low';
  if (n < 60) return 'moderate';
  return 'high';
}

function dnForAssetPolygonWithNearest(assetPoly, fireFC) {
  let dnIntersect = null;

  fireFC.features.forEach(f => {
    const isIntersectFeature = turf.intersect(assetPoly, f);

    if (isIntersectFeature) {
      dnIntersect = Number(f.properties?.DN);

      return;
    }
  });

  if (dnIntersect)
    return { dn: dnIntersect, dnBand: bandFromDN(dnIntersect), mode: 'intersect', distKm: 0 };
  else {
    const c = turf.centroid(assetPoly);
    let best = { distKm: Infinity, dn: undefined };

    fireFC.features.forEach(f => {
      const line = turf.polygonToLine(f);
      const snap = turf.nearestPointOnLine(line, c);
      const dKm = turf.distance(c, snap, { units: 'kilometers' });
      const dnVal = Number(f.properties?.DN);
      if (dKm < best.distKm && Number.isFinite(dnVal)) {
        best = { distKm: dKm, dn: dnVal };
      }
    });

    if (best.distKm <= MAX_DN_PROXIMITY_KM && Number.isFinite(best.dn)) {
      return { dn: best.dn, dnBand: bandFromDN(best.dn), mode: 'nearest', distKm: best.distKm };
    }

    return { dn: undefined, dnBand: undefined, mode: 'none', distKm: Infinity };
  }
}

export function ensureAssetPaintOverlay(map) {
  if (!map) return;

  if (!map.getSource('asset-paint-src')) {
    map.addSource('asset-paint-src', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  }

  if (!map.getLayer('asset-paint-fill')) {
    map.addLayer({
      id: 'asset-paint-fill',
      type: 'fill',
      source: 'asset-paint-src',
      paint: {
        'fill-color': ['coalesce', ['get', 'polyFill'], ['get', 'fill'], '#268fff'],
        'fill-opacity': 0.7
      }
    });
  }

  if (!map.getLayer('asset-paint-line')) {
    map.addLayer({
      id: 'asset-paint-line',
      type: 'line',
      source: 'asset-paint-src',
      paint: {
        'line-color': ['coalesce', ['get', 'bufOutline'], ['get', 'stroke'], '#00137C'],
        'line-width': 1
      }
    });
  }
}

export function refreshAssetPaintOverlay(map, draw) {
  if (!map || !draw || !map.getSource('asset-paint-src')) return;

  const all = draw.getAll()?.features || [];
  const feats = all
    .filter(f =>
      (f?.properties?.assetContour || f?.properties?.isBuilding) &&
      (f?.geometry?.type === 'Polygon' || f?.geometry?.type === 'MultiPolygon') &&
      isValidPolygonGeom(f.geometry)
    )
    .map(f => ({
      type: 'Feature',
      id: f.id,
      geometry: f.geometry,
      properties: {
        bufFill: f.properties?.riskBufferFill || null,
        bufOutline: f.properties?.riskBufferOutline || null,
        fill: f.properties?.riskFillColor || null,
        stroke: f.properties?.riskLineColor || null,
      }
    }));

  const fc = { type: 'FeatureCollection', features: feats };
  map.getSource('asset-paint-src').setData(fc);
}

export function clearAssetPaintOverlay(map) {
  if (!map) return;
  if (map.getLayer('asset-paint-line')) map.removeLayer('asset-paint-line');
  if (map.getLayer('asset-paint-fill')) map.removeLayer('asset-paint-fill');
  if (map.getSource('asset-paint-src')) map.removeSource('asset-paint-src');
}